import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import {
  DropDownOption,
  ExtendedUnitFilterOptions,
  PriceRange,
  ProjectIdentity,
  PropertyConfigurationInterface,
  RootStateTypeExtra,
} from '@store/types'

import ToggleSwitch from '@components/toggle-switch'
import VerticalScrollContainer from '@components/vertical-scroll-container'

import { Level } from '@api/building'
import { MappingBlockCollection } from '@api/interactive-plan'
import { FilterRemote } from '@api/white-label'

import useBlockLevelData from '@utilities/use-block-level-data'
import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { CloseSvg } from '@svg/react'

import {
  Unit,
  UnitFilterOptionsConfigurations,
} from '@adUtilities/types/apartment'
import { MinMaxInterface } from '@adUtilities/types/common'

import DropDown from '../dropdown'
import { CheckboxInput } from '../form-controls'
import { DEFAULT_ANY_OPTION } from './constants'
import FILTER_INITIAL_STATE from './filterState'
import RangeOptions from './range-options'

interface FilterProps {
  isOpen: boolean
  toggle: (arg: boolean) => void
  firebaseUnitFilter: ExtendedUnitFilterOptions
  levels: Array<Level>
  blocks: MappingBlockCollection
  prices: PriceRange
  showPrice: boolean
  hideLevel?: boolean
  isEnvisionFilter?: boolean
  aspects: Array<string>
  unitTypes: Array<string>
  propertyConfig: PropertyConfigurationInterface
  projectIdentity: ProjectIdentity
  firebaseActiveBlock: string
  firebaseActiveLevel: string
  availableStatusLabel: string
  filterWhiteLabel: FilterRemote
}
interface FilterPayload {
  [key: string]: string | boolean
}

function Filter({
  isOpen,
  toggle,
  firebaseUnitFilter,
  blocks,
  levels,
  prices,
  showPrice,
  aspects,
  unitTypes,
  propertyConfig,
  projectIdentity,
  firebaseActiveBlock,
  firebaseActiveLevel,
  hideLevel,
  isEnvisionFilter,
  availableStatusLabel,
  filterWhiteLabel,
}: FilterProps) {
  const AVAILABLE_STATUS = 'available'

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })
  useBlockLevelData({ projectIdentity })

  const [showAvailable, setShowAvailable] = useState(
    firebaseUnitFilter.showAvailable
  )
  const [activeLevel, setActiveLevel] = useState(firebaseActiveLevel)
  const [checkAnyLevel, setAnyLevel] = useState(false)
  const [range, setRange] = useState<MinMaxInterface>(firebaseUnitFilter.range)
  const [configurations, setConfigurations] =
    useState<UnitFilterOptionsConfigurations>(firebaseUnitFilter.configurations)
  const [firebaseAspects, setFirebaseAspects] = useState<Array<string>>(
    firebaseUnitFilter.aspect
  )
  const [firebaseUnitType, setFirebaseUnitType] = useState<string>(
    firebaseUnitFilter.unitType
  )

  const filterRef = useRef<HTMLDivElement>(null)

  const getLevelOptions = useMemo(() => {
    const levelList: Level[] = [...levels]

    const filteredLevels = levelList.filter((lvl) =>
      showAvailable
        ? lvl.data.some(
            (unit: Unit) =>
              unit.metas.status === availableStatusLabel ||
              unit.metas.status === AVAILABLE_STATUS
          )
        : true
    )

    const levelItems: Array<DropDownOption> = []
    filteredLevels.forEach((res) => {
      if (firebaseActiveBlock && blocks && Object.keys(blocks).length > 1) {
        if (res.data.find((lvl) => lvl.blockId === firebaseActiveBlock)) {
          levelItems.push({
            label: res.level,
            value: res.level,
          })
        }
      } else {
        levelItems.push({
          label: res.level,
          value: res.level,
        })
      }
    })

    return levelItems
  }, [levels, firebaseActiveBlock, showAvailable, availableStatusLabel])

  const unitTypeOptions = useMemo(
    () =>
      unitTypes?.map((item) => ({
        label: item,
        value: item,
      })),
    [unitTypes]
  )

  const handleRangeChange = (rangeValue: { max: string; min: string }) =>
    firebaseControlQuery.update({
      [`building.unitFilter.range`]: rangeValue,
      [`building.unitFilter.apply`]: false,
    })

  const handleAnyLevel = (checked: boolean) =>
    firebaseControlQuery.update({
      [`building.unitFilter.anyLevel`]: checked,
      [`building.unitFilter.apply`]: false,
    })

  const handleConfigurationOnchange = (
    configs: UnitFilterOptionsConfigurations
  ) =>
    firebaseControlQuery.update({
      [`building.unitFilter.configurations`]: configs,
      [`building.unitFilter.apply`]: false,
    })

  const handleAspectOnChange = (value: string) => {
    if (value === DEFAULT_ANY_OPTION) {
      firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: [],
      })
      return
    }

    if (firebaseAspects.includes(value)) {
      firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: firebaseAspects.filter(
          (item) => item !== value
        ),
        [`building.unitFilter.apply`]: false,
      })
    } else {
      firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: [...firebaseAspects, value],
        [`building.unitFilter.apply`]: false,
      })
    }
  }

  const handleUnitTypeOnchange = (value: string) =>
    firebaseControlQuery.update({
      [`building.unitFilter.unitType`]: value,
      [`building.unitFilter.apply`]: false,
    })

  const handleShowAvailable = (checked: boolean) =>
    firebaseControlQuery.update({
      [`building.unitFilter.showAvailable`]: checked,
      [`building.unitFilter.apply`]: false,
    })

  const applyFilter = () => {
    const filterPayload: FilterPayload = {
      [`building.unitFilter.apply`]: true,
      [`building.unitFilter.anyLevel`]: false,
    }
    if (!hideLevel) {
      filterPayload['building.activeLevel'] = activeLevel
    }

    if (isEnvisionFilter) {
      filterPayload['envisionVR.activeBuilding'] = firebaseActiveBlock
    }
    firebaseControlQuery.update(filterPayload)
    toggle(false)
  }

  const resetFilter = async () => {
    firebaseControlQuery.update({
      [`building.unitFilter`]: FILTER_INITIAL_STATE,
    })
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target as Node) &&
      isOpen
    ) {
      toggle(false)
    }
  }

  const hasAnyPropertyConfig = useMemo(
    () => Object.values(propertyConfig).some((property) => property.length > 0),
    [propertyConfig]
  )

  useEffect(() => {
    setConfigurations(firebaseUnitFilter.configurations)
    setFirebaseAspects(firebaseUnitFilter.aspect)
    setFirebaseUnitType(firebaseUnitFilter.unitType)
    setRange(firebaseUnitFilter.range)
    setShowAvailable(firebaseUnitFilter.showAvailable)
    setAnyLevel(false)
  }, [firebaseUnitFilter])

  useEffect(() => {
    if (isOpen && firebaseUnitFilter.apply) {
      firebaseControlQuery.update({
        [`building.unitFilter.apply`]: false,
      })
    }
  }, [activeLevel])

  useEffect(() => {
    setActiveLevel(firebaseActiveLevel)
  }, [firebaseActiveLevel])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick, false)
    }, 0)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isOpen])

  return (
    <div
      ref={filterRef}
      className={`fixed inset-y-0 right-0 z-50 h-screen w-[20rem] rounded-bl-lg rounded-tl-lg bg-secondaryColour p-5 shadow-standard ipad-pro:w-[25rem] ipad-mini-land:w-[25rem] ${
        isOpen ? '' : 'translate-x-full'
      } transition-transform duration-500`}
    >
      <div className="flex h-screen flex-col text-mainColour">
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <h2 className="text-title font-medium">Filters</h2>

            <button
              className="cursor-pointer"
              type="button"
              onClick={() => {
                toggle(false)
              }}
            >
              <CloseSvg className="mr-2 h-4 w-4" strokeColor="currentColor" />
            </button>
          </div>
          <button
            className="text-xl font-normal text-[#808080] underline opacity-60"
            type="button"
            onClick={resetFilter}
          >
            Reset filters
          </button>
        </div>

        <VerticalScrollContainer className="mt-5 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5">
            {getLevelOptions.length > 0 && !hideLevel ? (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  {filterWhiteLabel.levelDropdownLabel}
                </span>
                <DropDown
                  onSelect={(value) => {
                    setActiveLevel(value)
                  }}
                  disabled={checkAnyLevel}
                  items={getLevelOptions}
                  value={activeLevel}
                  className={checkAnyLevel ? 'opacity-60' : ''}
                />

                <div className="hidden items-center gap-3">
                  <ToggleSwitch
                    key="offlineToggleSwitch"
                    id="offlineToggleSwitch"
                    onChangeCallback={(e) => {
                      handleAnyLevel(e.target.checked)
                    }}
                    initialState={checkAnyLevel}
                    onColor="bg-mainColour"
                    offColor="bg-grayColour"
                  />
                  <span className="text-xl text-mainColour">Any Level</span>
                </div>
              </div>
            ) : (
              ''
            )}

            {showPrice && (
              <RangeOptions
                field="price"
                options={prices}
                range={range}
                handleChange={handleRangeChange}
              />
            )}

            {hasAnyPropertyConfig && (
              <span className="text-default font-medium uppercase">
                {filterWhiteLabel.configuration}
              </span>
            )}

            {Object.entries(propertyConfig)?.map(
              ([field, value]) =>
                value?.length > 0 && (
                  <RangeOptions
                    field={field}
                    key={field}
                    options={value as unknown as Array<string | number>}
                    range={
                      configurations[
                        field as keyof UnitFilterOptionsConfigurations
                      ]
                    }
                    handleChange={(configValue: MinMaxInterface) => {
                      handleConfigurationOnchange({
                        ...configurations,
                        [field as keyof UnitFilterOptionsConfigurations]:
                          configValue,
                      })
                    }}
                  />
                )
            )}

            {unitTypeOptions.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Unit Types
                </span>
                <DropDown
                  onSelect={handleUnitTypeOnchange}
                  items={[
                    { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                    ...unitTypeOptions,
                  ]}
                  value={firebaseUnitType}
                />
              </div>
            )}

            {aspects && aspects.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <span className="text-default font-medium uppercase">
                  Aspect
                </span>
                <DropDown
                  onSelect={(item) => handleAspectOnChange(item)}
                  items={[
                    { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                    ...aspects.map((item) => ({
                      value: item,
                      label: item,
                    })),
                  ]}
                  value={
                    firebaseAspects.length === 0
                      ? DEFAULT_ANY_OPTION
                      : firebaseAspects.join(', ')
                  }
                />
              </div>
            )}
          </div>
        </VerticalScrollContainer>

        <div className="mb-8 mt-4">
          <div className="flex items-center gap-2">
            <CheckboxInput
              initialState={showAvailable}
              onChangeCallback={(e) => {
                handleShowAvailable(e.target.checked)
              }}
              onColor="bg-mainColour"
              offColor="bg-neutralColour"
              id="availableResidence"
            />
            <span className="text-xl">{filterWhiteLabel.toggleText}</span>
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={applyFilter}
              disabled={firebaseUnitFilter.apply}
              className={`w-full rounded-lg bg-gradient p-4 text-center text-subHeading font-medium text-white shadow-standard ${
                firebaseUnitFilter.apply
                  ? 'cursor-not-allowed opacity-40'
                  : 'opacity-100'
              }`}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(
  ({
    projectIdentity: {
      prices,
      showPrice,
      propertyConfig,
      aspects,
      unitTypes,
      statusLabels,
    },
    projectIdentity,
    blockLevel: { blocks, levels },
    whiteLabel,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    prices,
    showPrice,
    propertyConfig,
    aspects,
    unitTypes,
    blocks,
    levels,
    availableStatusLabel: statusLabels?.available,
    filterWhiteLabel: mapWhiteLabelsWithDefaults(whiteLabel).remote.filter,
  })
)(Filter)
